import React, { useEffect } from 'react';
import { toast } from 'sonner';
import { ShieldAlert, ShieldX, Lock, AlertTriangle } from 'lucide-react';

// Create a custom error class for permission errors
export class PermissionError extends Error {
  constructor(message, permission) {
    super(message);
    this.name = 'PermissionError';
    this.permission = permission;
  }
}

// Global error handler component
export function PermissionErrorHandler() {
  useEffect(() => {
    // Listen for global errors and unhandled promise rejections
    const handleError = (event) => {
      const error = event.error || event.reason;
      if (error && (isPermissionError(error) || error.message?.includes('Foreign key constraint'))) {
        // Prevent default browser error display
        event.preventDefault();
        event.stopPropagation();
        
        if (isPermissionError(error)) {
          showPermissionError(error);
        }
      }
    };

    const handleUnhandledRejection = (event) => {
      const error = event.reason;
      if (error && (isPermissionError(error) || error.message?.includes('Foreign key constraint'))) {
        // Prevent default browser error display
        event.preventDefault();
        
        if (isPermissionError(error)) {
          showPermissionError(error);
        }
      }
    };

    window.addEventListener('error', handleError, true); // Use capture phase
    window.addEventListener('unhandledrejection', handleUnhandledRejection, true);

    return () => {
      window.removeEventListener('error', handleError, true);
      window.removeEventListener('unhandledrejection', handleUnhandledRejection, true);
    };
  }, []);

  return null;
}

// Check if error is a permission error
export function isPermissionError(error) {
  if (!error) return false;
  
  const message = error.message || String(error);
  return (
    error instanceof PermissionError ||
    message.includes('Insufficient permissions') ||
    message.includes('permission') ||
    message.includes('forbidden') ||
    message.includes('Forbidden') ||
    message.toLowerCase().includes('not authorized')
  );
}

// Extract permission from error message
function extractPermission(errorMessage) {
  const match = errorMessage.match(/Required:\s*([a-z.]+)/i);
  return match ? match[1] : null;
}

// Show permission error with beautiful UI
export function showPermissionError(error) {
  const message = error.message || String(error);
  const permission = error.permission || extractPermission(message);
  
  // Parse permission into readable format
  const permissionParts = permission ? permission.split('.') : [];
  const resource = permissionParts[0] || 'resource';
  const action = permissionParts[1] || 'perform this action';
  
  // Capitalize first letter
  const resourceName = resource.charAt(0).toUpperCase() + resource.slice(1);
  const actionName = action === 'edit' ? 'edit' : action === 'view' ? 'view' : action === 'create' ? 'create' : action === 'delete' ? 'delete' : action;

  toast.error(
    <div className="flex items-start gap-3 pr-4">
      <div className="flex-shrink-0 mt-0.5">
        <div className="relative permission-error-icon">
          <ShieldX className="h-6 w-6 text-red-500" />
          <div className="absolute -top-1 -right-1 permission-lock-icon">
            <Lock className="h-3 w-3 text-red-600" />
          </div>
        </div>
      </div>
      <div className="flex-1 space-y-1.5">
        <p className="font-bold text-base text-slate-900 flex items-center gap-2">
          Access Denied
          <AlertTriangle className="h-4 w-4 text-amber-500" />
        </p>
        <p className="text-sm text-slate-700 leading-relaxed">
          You don't have permission to <span className="font-semibold text-red-600">{actionName}</span>{' '}
          <span className="font-semibold text-slate-900">{resourceName}</span>.
        </p>
        {permission && (
          <div className="permission-badge mt-2 px-3 py-1.5 bg-red-50 border border-red-200 rounded-md text-xs">
            <span className="text-red-800 font-mono font-medium">Missing Permission: {permission}</span>
          </div>
        )}
        <div className="flex items-center gap-2 mt-3 pt-2 border-t border-slate-200">
          <ShieldAlert className="h-3.5 w-3.5 text-slate-400" />
          <p className="text-xs text-slate-500">
            Contact your administrator to request access
          </p>
        </div>
      </div>
    </div>,
    {
      duration: 7000,
      position: 'top-center',
      className: 'permission-error-toast permission-error-glow',
      style: {
        background: 'linear-gradient(135deg, #fef2f2 0%, #fff5f5 50%, #ffffff 100%)',
        border: '2px solid #fca5a5',
        boxShadow: '0 20px 50px rgba(239, 68, 68, 0.25), 0 10px 20px rgba(239, 68, 68, 0.15)',
        minWidth: '400px',
      },
      important: true,
    }
  );
}

// Hook for handling errors in components
export function usePermissionErrorHandler() {
  const handleError = (error) => {
    if (isPermissionError(error)) {
      showPermissionError(error);
      return true; // Handled
    }
    return false; // Not handled
  };

  return { handleError, showPermissionError, isPermissionError };
}

export default PermissionErrorHandler;




